import React, { useCallback, useEffect, useMemo } from 'react'
import useParams from 'core/hooks/useParams'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { listImages, updateImage } from './actions'
import ModalForm from 'core/elements/modal/ModalForm'
import TextField from 'core/components/validatedForm/TextField'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import { useSelector } from 'react-redux'
import { prop } from 'ramda'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import { RootState } from 'app/store'
import { isAdminRole } from 'k8s/util/helpers'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import { vtpmRadioOptions } from './AddImageModal'
import RadioFields from 'core/components/validatedForm/radio-fields'
import useListAction from 'core/hooks/useListAction'
import ExternalLink from 'core/components/ExternalLink'
import { noop } from 'utils/fp'

const useStyles = makeStyles<Theme>((theme) => ({}))

interface Props {
  rows: any[]
  onClose: () => void
}

const buildMetadataOps = (imageKeys: string[], enabled: boolean, key: string, value: string) => {
  const ops: any[] = []
  if (enabled) {
    if (imageKeys.includes(key)) {
      ops.push({
        op: 'replace',
        path: `/${key}`,
        value,
      })
    } else {
      ops.push({
        op: 'add',
        path: `/${key}`,
        value,
      })
    }
  } else {
    if (imageKeys.includes(key)) {
      ops.push({
        op: 'remove',
        path: `/${key}`,
      })
    }
  }
  return ops
}

export default function EditImageModal({ rows: [image], onClose }: Props) {
  const classes = useStyles()
  const defaultParams = {
    name: '',
    public: false,
    protected: false,
    enableVTPM: false,
    vtpmModel: 'tpm-tis',
    enableRescue: false,
  }

  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  useEffect(() => {
    updateParams({
      name: image.name,
      public: image.visibility === 'public',
      protected: image.protected,
      enableVTPM: image?.hw_tpm_version === '2.0',
      vtpmModel: image?.hw_tpm_model || 'tpm-tis',
      enableRescue: image?.hw_rescue_device === 'disk' && image?.hw_rescue_bus === 'scsi',
    })
  }, [])

  const { update, updating, error, reset } = useUpdateAction(updateImage)
  const { reload } = useListAction(listImages)

  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const isAdmin = useMemo(() => isAdminRole(session), [session])
  const isProtected = image?.protected

  const submitForm = useCallback(async () => {
    const baseOperations = [
      {
        op: 'replace',
        path: '/name',
        value: params.name,
      },
      {
        op: 'replace',
        path: '/visibility',
        value: params.public ? 'public' : 'private',
      },
      {
        op: 'replace',
        path: '/protected',
        value: params.protected,
      },
    ]

    const imageKeys = Object.keys(image)
    const conditionalOperations: any[] = []

    // vTPM version
    conditionalOperations.push(
      ...buildMetadataOps(imageKeys, params.enableVTPM, 'hw_tpm_version', '2.0'),
    )

    // vTPM model
    conditionalOperations.push(
      ...buildMetadataOps(imageKeys, params.enableVTPM, 'hw_tpm_model', params.vtpmModel),
    )

    // Rescue metadata
    conditionalOperations.push(
      ...buildMetadataOps(imageKeys, params.enableRescue, 'hw_rescue_device', 'disk'),
    )
    conditionalOperations.push(
      ...buildMetadataOps(imageKeys, params.enableRescue, 'hw_rescue_bus', 'scsi'),
    )

    const body = [...baseOperations, ...conditionalOperations]
    const { success } = await update({ id: image.id, body })
    if (!success) return
    reload(true)
    handleClose()
  }, [image?.id, isAdmin, params])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    onClose()
  }

  return (
    <ModalForm
      title={`Edit Image`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating}
      error={error}
      submitTitle={`Update Image`}
      open
    >
      <>
        <FormFieldSection title="Image Settings">
          <TextField
            id="name"
            label="Name"
            onChange={getParamsUpdater('name')}
            value={params.name}
            required
          />
          {!!isAdmin && (
            <CheckboxField
              id="public"
              label="Make Public"
              onChange={getParamsUpdater('public')}
              value={params.public}
              info="Public images will be available across all tenants. Private images will only be available within the same tenant."
              tooltipOffset={{
                vertical: 0,
                horizontal: -180,
              }}
            />
          )}
          <CheckboxField
            id="protected"
            label="Disable Delete (Protected)"
            onChange={!isAdmin && isProtected ? noop : getParamsUpdater('protected')}
            value={params.protected}
            disabled={!isAdmin && isProtected}
            info="Images that are marked as ‘protected’ can not be deleted."
            tooltipOffset={{
              vertical: 0,
              horizontal: -180,
            }}
          />
          <CheckboxField
            id="enableRescue"
            label="Use for rescuing VMs"
            onChange={getParamsUpdater('enableRescue')}
            value={params.enableRescue}
            info="When Use for rescuing VMs is enabled, this image will be used for VM rescue operations."
            tooltipOffset={{
              vertical: 0,
              horizontal: -180,
            }}
          />
          <CheckboxField
            id="enableVTPM"
            label="Enable Virtual TPM 2.0"
            onChange={getParamsUpdater('enableVTPM')}
            value={params.enableVTPM}
            info={
              <>
                Enable vTPM for virtual machines created using this image. Read{' '}
                <ExternalLink url="https://platform9.com/docs/private-cloud-director/private-cloud-director/virtual-tpm">
                  here
                </ExternalLink>{' '}
                for more info
              </>
            }
            tooltipOffset={{
              vertical: 0,
              horizontal: -180,
            }}
          />
          {params.enableVTPM && (
            <RadioFields
              title="Virtual TPM Model"
              id="vtpmModel"
              options={vtpmRadioOptions}
              value={params.vtpmModel}
              onChange={getParamsUpdater('vtpmModel')}
            />
          )}
        </FormFieldSection>
      </>
    </ModalForm>
  )
}
